import React, { FC, useCallback, useState } from "react";

import {
    UpdateAppInput,
    UserProfile,
    useUpdateApplicationMutation,
} from "generated/graphql";
import { OnboardingPropsBeta } from "models";
import { wrapUrqlMutation } from "utils/graphql";
import { useBoolState } from "utils/bool";
import { cleanObj } from "utils/Json";

import { SocialQuestionStep, SocialQuestionStepProps } from "../components/SocialQuestionStep";
import { OnboardingProps } from "../models";

export interface SocialQuestionStepContainerProps extends Omit<SocialQuestionStepProps, "onNext"> {
    /**
     * Site data used to prepopulate fields
     */
    initialProps?: OnboardingProps;
    /**
     * Profile of the user
     */
    profile: UserProfile;
    /**
     * Selected product
     */
    product: string;
    /**
     * The user is ready for the next step
     */
    onNextStep(props: OnboardingProps): void;
}

export const SocialQuestionStepContainer: FC<SocialQuestionStepContainerProps> = props => {

    const { onNextStep, initialProps } = props;

    const [creationStatusMessage, setCreationStatusMessage] = useState<string>();
    const [creating, stopCreating, startCreating] = useBoolState(false);
    const [error, setError] = useState<{ error: string, message: string }>();

    const updateApplication = wrapUrqlMutation(useUpdateApplicationMutation(), {
        onSuccessMessage: false,
        onErrorMessage: (errors: string[]): { send: boolean, message?: string } => {
            // Concatenate the messages
            const error = errors.join(", ");
            setError({ error, message: "We're sorry, we had trouble updating the contact information on the app for you." });
            return { send: false }
        }
    });

    /**
     * If they have a website, start the crawl & analysis, then move on
     */
    const handleNext = useCallback(async (hasSocial: boolean, socialSite?: string) => {
        startCreating();
        setCreationStatusMessage("Thank you, preparing next step...");

        const appProps = cleanObj(initialProps);

        appProps.beta = {
            ...appProps.beta,
            onboarding: {
                ...appProps.beta?.onboarding,
                hasSocial
            }
        };

        const updateAppProps: UpdateAppInput = {
            beta: {
                ...appProps.beta,
                onboarding: {
                    ...appProps.beta?.onboarding,
                    hasSocial,

                    socialSite,
                    lastStep: "social",
                } as OnboardingPropsBeta
            },
        };
        await updateApplication({
            appId: appProps.appId,
            app: updateAppProps
        });
        // Update the current fields in case of an error
        sessionStorage.setItem("onboarding-appProps", JSON.stringify(appProps));

        stopCreating();

        // Move on!
        onNextStep(appProps);
    }, [initialProps, onNextStep, startCreating, stopCreating, updateApplication]);

    return (
        <SocialQuestionStep
            {...props}
            // hasWebsite={initialProps?.website ? true : false}
            updating={creating}
            updatingStatusMessage={creationStatusMessage}
            onNext={handleNext}
            error={error}
        />
    );
}